/**
 * creditsHelpers.js — RC 1.38.B.1 Commercial Transparency (corrected).
 *
 * Pure helpers split out from `creditsContext.js` so they can be
 * unit-tested without React / react-router-dom resolution under
 * `craco test`.
 *
 * Action cost keys come from `core/usage.py::USAGE_COSTS` and are
 * exposed via `GET /api/account/plan .usage_costs`. The legacy
 * keys from `/api/credits/me` (`analyze_fit_ndor`,
 * `generate_cv_variant`, etc.) are NOT used anywhere in this slice.
 */

/**
 * Look up the per-action usage cost for the given action key.
 *
 * @param {Record<string, number>|null|undefined} usageCosts
 * @param {'analyze_fit'|'cv_improve'|'cover_letter'|'application_pack'|string} action
 * @returns {number|null}
 */
export function getActionCost(usageCosts, action) {
  if (!usageCosts || typeof usageCosts !== 'object') return null;
  const v = usageCosts[action];
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;
}

/**
 * Total usage remaining (included + extra). Returns `null` while
 * the provider is bootstrapping; `0` for an exhausted user.
 */
export function totalUsageRemaining({ includedUsageRemaining, extraUsageRemaining }) {
  if (
    typeof includedUsageRemaining !== 'number'
    && typeof extraUsageRemaining !== 'number'
  ) {
    return null;
  }
  return (Number(includedUsageRemaining) || 0) + (Number(extraUsageRemaining) || 0);
}

/** Focus / Flow plans gate spending against included + extra. */
export function isPaidPlan(planCode) {
  return planCode === 'focus' || planCode === 'flow';
}

/**
 * Should the action be gated due to insufficient usage?
 * Only true for paid plans (where included+extra is the funding
 * source). Base users are governed by the daily free-fit limit
 * separately.
 */
export function isInsufficient({ planCode, includedUsageRemaining, extraUsageRemaining, cost }) {
  if (!isPaidPlan(planCode)) return false;
  if (typeof cost !== 'number' || cost <= 0) return false;
  const total = totalUsageRemaining({ includedUsageRemaining, extraUsageRemaining });
  if (typeof total !== 'number') return false;
  return total < cost;
}

/**
 * Format a usage count for the pill / labels. `null` → "—" so the
 * pill still renders during the bootstrap fetch.
 */
export function formatBalance(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
}

/**
 * RC 1.48.7B (2026-05-04) — total Credit cost of a fully-tailored
 * application (Analyse Fit + Tailored CV + Cover letter).
 *
 * Derived live from `usageCosts` so any admin price tune in
 * `credit_action_prices` propagates to every surface that shows
 * the "Covers roughly N fully-tailored applications per month"
 * strapline without a redeploy.
 *
 * Returns `null` if any of the three component prices is missing
 * so call sites can fall back to "—" or suppress the strapline
 * entirely rather than render a stale hardcoded number.
 *
 * Replaces the pre-RC 1.48.7B hardcoded
 * `FULLY_TAILORED_APP_COST_CREDITS = 25` constant that used to be
 * duplicated in `AccountPlanSection.js` + `OnboardingPlanPage.js`.
 *
 * @param {Record<string, number>|null|undefined} usageCosts
 * @returns {number|null}
 */
export function getFullyTailoredAppCost(usageCosts) {
  const af = getActionCost(usageCosts, 'analyze_fit');
  const cv = getActionCost(usageCosts, 'cv_improve');
  const cl = getActionCost(usageCosts, 'cover_letter');
  if (af === null || cv === null || cl === null) return null;
  const total = af + cv + cl;
  return total > 0 ? total : null;
}

